import { defineStore, acceptHMRUpdate } from 'pinia';

// The floating assistant panel: open/closed, plus the page context that
// rides with each message. Context arrives two ways -- from the route
// (the record the user is viewing; replaced on every navigation to a
// detail page) and from an explicit pin (it survives a navigation to a
// list page, where nothing is in focus).
const useAssistantPanelStore = defineStore('assistantPanel', {
  state: () => ({
    isOpen: false,
    // 'panel' (the side sheet) or 'full' (the main content area, with
    // the app sidebar and header staying put). Minimizing keeps the
    // conversation; REOPENING always returns as the side panel.
    mode: 'panel',
    // the chat mounts on first open and then stays alive behind v-show,
    // so closing the panel does not lose the conversation
    hasOpened: false,
    // {kind, id|external_id, ids, label} or null
    context: null,
    pinned: false,
    // the latest page descriptor from the route/selection watcher --
    // what "New chat" falls back to (includes a live selection, which
    // route parsing alone would drop)
    lastPageContext: null,
    // which company the panel's state belongs to, and a generation
    // counter stamped onto every in-flight request so a late response
    // from a previous company (or a pre-switch send) is discarded
    // instead of rendering under the new company
    companyId: null,
    generation: 0,
    // bumped when a confirmed action changed the record the page behind
    // the panel is showing; the router view keys off it and remounts,
    // refetching fresh data (the panel lives outside it and survives)
    pageRefreshTick: 0,
  }),
  actions: {
    open(context = null) {
      if (context) {
        this.context = context;
        this.pinned = true;
      }
      this.isOpen = true;
      this.hasOpened = true;
      // reopening ALWAYS returns as the side panel, whatever state the
      // panel was minimized from
      this.mode = 'panel';
    },
    close() {
      this.isOpen = false;
    },
    maximize() {
      this.mode = 'full';
    },
    restore() {
      this.mode = 'panel';
    },
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    // called on every navigation/selection change with the page context
    setPageContext(context) {
      this.lastPageContext = context;

      if (context) {
        this.context = context;
        this.pinned = false;
      } else if (!this.pinned) {
        this.context = null;
      }
    },
    clearContext() {
      this.context = null;
      this.pinned = false;
    },
    // "New chat": drop any pin and fall back to whatever page is open,
    // including a live Work Manager selection
    resetToPage() {
      this.pinned = false;
      this.context = this.lastPageContext;
    },
    // a company switch resets every tenant-bound piece of state and
    // bumps the generation so any in-flight response is quarantined
    switchCompany(companyId) {
      if (this.companyId === companyId) return false;

      this.companyId = companyId;
      this.generation += 1;
      this.context = null;
      this.lastPageContext = null;
      this.pinned = false;
      return true;
    },
    // fill in the display name resolved server-side, but only while the
    // chip still shows the same record -- a stale response arriving after
    // a navigation must not mislabel the new context
    setContextLabel(descriptor, label) {
      if (this.context
        && this.context.kind === descriptor.kind
        && this.context.id === descriptor.id) {
        this.context = { ...this.context, label };
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useAssistantPanelStore, import.meta.hot),
  );
}

export { useAssistantPanelStore };
